import {
  Activity,
  CreateEntranceTicketTypeInput,
  CreateNewsPostInput,
  CreatePackInput,
  EditEntranceTicketTypeInput,
  EditNewsPostInput,
  EditPackInput,
  EntranceTicketType,
  EntranceTicketTypeBackOffice,
  NewsPost,
  Pack,
  PromotionOptions,
} from 'apollo/types';

export const DEFAULT_LANGUAGE = 'es';
export const TRANSLATION_LANGUAGES = ['en'] as const;
export const LANGUAGES = [DEFAULT_LANGUAGE, ...TRANSLATION_LANGUAGES] as const;

export type DefaultLanguage = typeof DEFAULT_LANGUAGE;
export type TranslationLanguage = typeof TRANSLATION_LANGUAGES[number];
export type Language = typeof LANGUAGES[number];

export type LocalizedText = Record<DefaultLanguage, string> &
  Partial<Record<TranslationLanguage, string | null>>;

export interface LocalizedNewsPostContent {
  order: number;
  content: LocalizedText;
}

export interface LocalizedNewsPostRecommendation {
  id: string;
  title: LocalizedText;
}

export type LocalizedNewsPost = Omit<
  NewsPost,
  'title' | 'contents' | 'newsPostRecommendations'
> & {
  title: LocalizedText;
  contents?: LocalizedNewsPostContent[] | null;
  newsPostRecommendations: LocalizedNewsPostRecommendation[];
};

export interface LocalizedContentInput {
  order: number;
  content: LocalizedText;
}

export type LocalizedCreateNewsPostInput = Omit<
  CreateNewsPostInput,
  'title' | 'contents'
> & {
  title: LocalizedText;
  contents: LocalizedContentInput[];
};

export type LocalizedEditNewsPostInput = Omit<EditNewsPostInput, 'title' | 'contents'> & {
  title?: LocalizedText;
  contents?: LocalizedContentInput[];
};

export type LocalizedEntranceTicketType = Omit<
  EntranceTicketType,
  'name' | 'description'
> & {
  name: LocalizedText;
  description?: LocalizedText | null;
};

export type LocalizedEntranceTicketTypeBackOffice = Omit<
  EntranceTicketTypeBackOffice,
  'name' | 'enName' | 'enDescription' | 'description'
> & {
  name: LocalizedText;
  description?: LocalizedText | null;
};

export type LocalizedPack = Omit<
  Pack,
  'name' | 'enName' | 'enDescription' | 'description' | 'entranceTicketTypes'
> & {
  name: LocalizedText;
  description?: LocalizedText | null;
  entranceTicketTypes: LocalizedEntranceTicketType[];
};

export type LocalizedCreatePackInput = Omit<
  CreatePackInput,
  'name' | 'enName' | 'enDescription' | 'description'
> & { name: LocalizedText; description?: LocalizedText };

export type LocalizedEditPackInput = Omit<
  EditPackInput,
  'name' | 'enName' | 'enDescription' | 'description'
> & { name?: LocalizedText; description?: LocalizedText };

export type LocalizedPromotionOptions = Omit<
  PromotionOptions,
  'entranceTicketTypes' | 'packs'
> & {
  entranceTicketTypes?: LocalizedEntranceTicketType[] | null;
  packs?: LocalizedPack[] | null;
};

export type LocalizedTicketTypes = {
  listActivitiesBackOffice: Activity[];
  listEntranceTicketTypesBackOffice: LocalizedEntranceTicketTypeBackOffice[];
};

export type LocalizedCreateEntranceTicketTypeInput = Omit<
  CreateEntranceTicketTypeInput,
  'name' | 'enName' | 'enDescription' | 'description'
> & { name: LocalizedText; description?: LocalizedText };

export type LocalizedEditEntranceTicketTypeInput = Omit<
  EditEntranceTicketTypeInput,
  'name' | 'enName' | 'enDescription' | 'description'
> & { name?: LocalizedText; description?: LocalizedText };
